import { useQuery } from "react-query";
import config from "../../config";
import { apiVersion } from "../../shared/constants/common.constant";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import { getAsync } from "../protect-api";

export const RECHARGE_SUMMARY_KEY = "recharge-summary";

interface RechargeSummaryApiResponse {
  activeSchedulesCount: number;
  hasUnlimitedScreeningCredits: boolean;
  availableScreeningCredits: number;
  hasUnlimitedMonitoringCredits: boolean;
  availableMonitoringCredits: number;
  rechargeDueDate: string;
  minTopUpToRenew: number;
}

export interface RechargeSummaryResponse {
  availableScreeningCredits: number | "unlimited";
  availableMonitoringCredits: number | "unlimited";
  rechargeDueDate: string;
  minTopupToRenew: number;
  apiFailed?: boolean;
}

export const getRechargeSummary =
  async (): Promise<RechargeSummaryResponse> => {
    try {
      const { data } = await getAsync<RechargeSummaryApiResponse>(
        `${config.COMPLIANCE_API_URL}/${apiVersion}/recharge/summary`,
        undefined,
        undefined,
        true
      );

      return {
        availableScreeningCredits: data.hasUnlimitedScreeningCredits
          ? "unlimited"
          : data.availableScreeningCredits ?? 0,
        availableMonitoringCredits: data.hasUnlimitedMonitoringCredits
          ? "unlimited"
          : data.availableMonitoringCredits ?? 0,
        rechargeDueDate: data.rechargeDueDate ?? "",
        minTopupToRenew: data.minTopUpToRenew ?? 0,
        apiFailed: false,
      };
    } catch {
      return {
        availableScreeningCredits: 0,
        availableMonitoringCredits: 0,
        rechargeDueDate: "",
        minTopupToRenew: 0,
        apiFailed: true,
      };
    }
  };

export const useGetRechargeSummary = (
  enabled = false
): ReturnType<typeof useQuery<RechargeSummaryResponse, ProblemDetails>> => {
  return useQuery<RechargeSummaryResponse, ProblemDetails>(
    [RECHARGE_SUMMARY_KEY],
    () => getRechargeSummary(),
    {
      enabled,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};
